import {Grid, Box, Typography} from '@mui/material';
import {ImagePreview} from '../dermaDetails/bodyLocations/imagePreview';
import heic2any from 'heic2any';
import {useDropzone} from 'react-dropzone';
import {useState, useContext, useEffect, useRef} from 'react';
import APIServiceFormContext from '../../context/APIServiceFormContext';
import CloseIcon from '@mui/icons-material/Close';
import {consistentSpacing} from './variables';
import {truncateToDecimal} from '../../utils/miscellaneous';
import {AnimationLoadingScreenWithProgressBar} from '../loading/loadingScreen';
import {delay} from '../../utils/miscellaneous';

const maxPhotoSizeBytes = 5_000_000;

/**
 * Upload pictures question component
 * @param {Object} props - Component props
 * @param {string} props.title
 * @param {Function} props.apiHook - useApi hook that uses axios for the processing callback function of the image
 * @param {string?} props.subtitle
 * @param {Object} props.useDropzoneProps - Object that is passed to the useDropzone hook
 * @param {Function} props.processingImageCallback - Function to process each image that is uploaded
 * @param {Number} props.maximumAllowedFiles
 *
 * @returns {React.Component}
 */
export const UploadPicturesQuestionComponent = ({
  title,
  subtitle,
  processingImageCallback,
  useDropzoneProps,
  maximumAllowedFiles,
  apiHook,
}) => {
  const {setValidationFunc, setSendDataArgs, savedValue, additionalSavedData} = useContext(APIServiceFormContext);
  const [imageURLs, setImageURLs] = useState([]);
  const useApiHook = apiHook();

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [responseReturned, setResponseReturned] = useState(false);
  const [previousUploadedFilesLength, setPreviousUploadedFilesLength] = useState(0);

  const [instantError, setInstantError] = useState(undefined);

  const componentValidationWrapper = () => {
    function componentValidation() {
      if (uploadedFiles.length === 0)
        return `Please upload only a file that is smaller than${truncateToDecimal(
          maxPhotoSizeBytes / 1_000_000,
          1,
        )}MB. `;

      return '';
    }

    return componentValidation;
  };
  const timeoutRef = useRef(null);
  useEffect(() => {
    if (savedValue !== undefined && savedValue !== null) setImageURLs(savedValue);

    if (additionalSavedData === undefined) return;
    if (additionalSavedData.previousFilesLength !== undefined)
      setPreviousUploadedFilesLength(additionalSavedData.previousFilesLength);
    if (additionalSavedData.uploadedFiles) setUploadedFiles(additionalSavedData.uploadedFiles);
  }, [savedValue, additionalSavedData]);
  useEffect(() => {
    clearTimeout(timeoutRef.current);
    setValidationFunc(func => {
      let newFunc = func.bind({}); //way to copy a function
      newFunc.instantError = instantError;
      return newFunc;
    });
    if (instantError === undefined) return;
    timeoutRef.current = setTimeout(() => {
      setInstantError(undefined);
    }, 4000);
    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, [instantError]);

  useEffect(() => {
    // updating sendDataArgs and instant error in case of asynchrony issues
    setValidationFunc(() => {
      let newFunc = componentValidationWrapper().bind({});
      newFunc.instantError = instantError;
      return newFunc;
    });
    setSendDataArgs({
      data: imageURLs,
      additional: {
        previousUploadedFilesLength,
        uploadedFiles,
      },
    });
  }, [imageURLs, uploadedFiles, previousUploadedFilesLength]);

  //custom dropzone code
  const onDrop = async (acceptedFiles, rejectedFiles) => {
    //creating urls for previewing and assigning those to the actual array of files so URLs can be accessed when iterating. also creating the dermadetect image routes
    //starts validating the image
    if (rejectedFiles.length > 0 || uploadedFiles.length === maximumAllowedFiles) {
      setInstantError(
        `You can only upload one file that is smaller than ${
          useDropzoneProps?.maxSize === undefined
            ? Math.round(maxPhotoSizeBytes / 1_000_000)
            : Math.round(useDropzoneProps.maxSize / 1_000_000)
        } MB`,
      );
      return;
    }

    setLoading(true);
    let imageValidity = {allImagesAreValid: true, currentImageValid: true};
    const filesWithRoute = await Promise.all(
      acceptedFiles.map(async file => {
        const url = URL.createObjectURL(file);
        file.url = url;

        const onLoadPromise = new Promise(async resolve => {
          if (/^image\/(heic|heif)$/.test(file.type)) {
            const fileToBlob = await heic2any({
              blob: file,
              toType: 'image/jpeg',
            });
            file.url = URL.createObjectURL(fileToBlob);
          }

          await processingImageCallback(imageValidity, file, resolve, setImageURLs, useApiHook);
        });

        await onLoadPromise; // Wait for the reader.onload to finish before proceeding

        const output = imageValidity.currentImageValid ? file : undefined;
        imageValidity.currentImageValid = true; //for the next image's default value
        return output;
      }),
    );

    const previousFilesLength = uploadedFiles.length;
    setPreviousUploadedFilesLength(previousFilesLength);
    let newFiles = uploadedFiles.concat(
      filesWithRoute.filter(f => f !== undefined), // so it doesn't consider overweight photos
    );

    setUploadedFiles(newFiles);

    if (!imageValidity.allImagesAreValid || newFiles.length === 0) {
      setInstantError('An error happened');
      setPreviousUploadedFilesLength(newFiles.length);

      setResponseReturned(true);
      delay(2).then(() => {
        setLoading(false);
      });
    }
  };
  const {getRootProps, getInputProps, isDragActive} = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg', '.heic'],
      'image/png': [],
    },
    maxSize: maxPhotoSizeBytes,
    ...useDropzoneProps,
  });
  //dropzone styles
  const borderColor = '#C3C2C2';
  const altColor = '#5CA0D2';

  // conditional that will ensure the loading state works correctly
  if (uploadedFiles.length !== previousUploadedFilesLength && loading) {
    setPreviousUploadedFilesLength(uploadedFiles.length);

    setResponseReturned(true);
    delay(2).then(() => {
      setLoading(false);
    });
  }
  return (
    <Grid
      container
      justifyContent="top"
      alignItems="center"
      flexDirection="column"
      rowGap={consistentSpacing}
      sx={{
        backgroundColor: '#FBFBFB',
        px: {xs: 1, sm: 2},
        py: 2,
        borderRadius: 5,
        flex: 1,
        width: {
          xs: '100%',
          md: '600px',
          lg: '750px',
        },
      }}
    >
      <Grid item xs={12}>
        <Typography
          variant="h4"
          sx={{
            pt: 2,
            fontWeight: 800,
            textAlign: 'center',
            lineHeight: {xs: 1.2, sm: 1.5},
          }}
        >
          {title}
        </Typography>{' '}
      </Grid>

      <Grid item xs={12} sx={{width: '100%'}}></Grid>
      <Grid item>
        <Typography
          textAlign={'center'}
          sx={{
            fontWeight: 500,
            textAlign: {xs: 'justify', sm: 'center'},
            lineHeight: {xs: 1.2, sm: 1.5},
          }}
        >
          {subtitle}
        </Typography>
      </Grid>
      <Grid item container xs={12} mt={2}>
        <Box
          className="dropzone"
          {...getRootProps()}
          sx={{
            borderRadius: 5,
            border: !isDragActive ? `3px solid ${borderColor}` : `6px solid ${altColor}`,
            margin: 'auto',
            backgroundColor: !isDragActive ? '#FBFBFB' : 'rgba(0,0,0,0.1)',
            opacity: 2,
            py: 1.5,
            px: 5,
            width: '100%',
            boxSizing: 'border-box',
            textAlign: 'center',
            userSelect: 'none',
            cursor: 'pointer',
          }}
        >
          <input {...getInputProps()} />
          <Typography variant="h6" fontWeight={'bold'} lineHeight={1.2}>
            Drop your image{maximumAllowedFiles > 1 ? 's' : ''} here, or{' '}
            <span style={{color: altColor, display: 'inline'}}>upload</span>.
          </Typography>
          <Typography
            variant="subtitle2"
            mt={0.5}
            sx={{
              fontWeight: 400,
              fontSize: {
                xs: 14,
                sm: 16,
              },
            }}
          >
            We support PNG, HEIC and JPEG files smaller than {truncateToDecimal(maxPhotoSizeBytes / 1_000_000, 1)} MB.
          </Typography>
        </Box>
      </Grid>
      {loading && (
        <AnimationLoadingScreenWithProgressBar
          title={`Validating the image${maximumAllowedFiles > 1 ? '(s)...' : '...'}`}
          secondsToLoad={30}
          isAlmostComplete={responseReturned}
        />
      )}
      {uploadedFiles.length !== 0 && (
        <Grid
          item
          container
          rowGap={2}
          sx={{
            justifyContent: 'space-between',
          }}
        >
          {uploadedFiles.map((file, idx) => (
            <Grid
              item
              xs={12}
              sm={5.5}
              container
              key={idx}
              sx={{
                border: `1px solid #C3C2C2`,
                borderRadius: 5,
                justifyContent: 'space-between',
                overflow: 'hidden',
              }}
            >
              <Grid item xs={3} sx={{alignSelf: 'stretch', overflow: 'hidden'}}>
                <ImagePreview src={file.url} width={{xs: '100px', sm: '140px'}} height={'50px'} />
              </Grid>
              <Grid
                item
                xs={5}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Typography sx={{width: 'fit-content !important'}}>
                  {file.name.length > 10
                    ? file.name.substring(0, 4) + '...' + file.name.slice(file.name.lastIndexOf('.') + 1)
                    : file.name}
                </Typography>
              </Grid>
              <Grid
                xs={1}
                item
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 1,
                }}
              >
                <CloseIcon
                  sx={{
                    cursor: 'pointer',
                    fontSize: {
                      xs: 20,
                    },
                  }}
                  onClick={() => {
                    const newFiles = uploadedFiles.filter(f => f !== file);
                    const newImageURLS = imageURLs.filter((_, index) => index !== idx);
                    setImageURLs(newImageURLS);
                    setUploadedFiles(newFiles);
                    setPreviousUploadedFilesLength(newFiles.length);
                  }}
                />
              </Grid>
            </Grid>
          ))}
        </Grid>
      )}
    </Grid>
  );
};
